/**
 * Which smileys this reader actually uses.
 *
 * «Часто» used to be a list somebody chose once and hard-coded. The mobile
 * handoff makes it a personal statistic instead, and the same ranking then
 * drives three things at once: the row above the input (top ten), the reaction
 * pill in the long-press menu (top six), and the «Часто» tab in both pickers.
 * One ordering everywhere, so the smiley in the corner of your eye is in the
 * same place in all three.
 *
 * It lives in localStorage rather than on the server. It is a preference, not
 * content: it costs no schema, it is worthless to anyone else, and the handoff
 * asks for cross-device sync only as a nice-to-have. Moving it to the user
 * profile later means changing `load` and `persist` and nothing else.
 */
import { useSyncExternalStore } from 'react';
import { findSmiley, type Smiley } from './smileys.ts';

export type SmileyFrequency = Record<string, number>;

/**
 * Where a new reader starts, so «Часто» is useful before it knows anything —
 * an empty first tab teaches nobody what the set contains. Straight from the
 * handoff, and it stops mattering after a dozen messages.
 */
export const SEED: SmileyFrequency = {
  smile: 3,
  biggrin: 3,
  lol: 2,
  wink: 2,
  good: 2,
  yes3: 1,
  nea: 1,
  sad: 1,
  heart: 1,
  dance4: 1,
  hi: 1,
  thank_you2: 1,
};

const KEY = 'smileFreq.v1';

/**
 * Most used first; ties broken by slug so the order is stable rather than
 * whatever the object happened to remember. A count of zero and a slug that is
 * no longer in the set both drop out — the stored counts outlive any one
 * version of the pack.
 */
export function rankSlugs(frequency: SmileyFrequency): Smiley[] {
  return Object.entries(frequency)
    .filter(([, count]) => count > 0)
    .sort(([leftSlug, left], [rightSlug, right]) => right - left || leftSlug.localeCompare(rightSlug))
    .map(([slug]) => findSmiley(slug))
    .filter((smiley): smiley is Smiley => smiley !== undefined);
}

function load(): SmileyFrequency {
  try {
    const raw = localStorage.getItem(KEY);
    // Merged over the seed rather than replacing it: someone who has only ever
    // sent two smileys still gets a full first screen.
    return raw ? { ...SEED, ...(JSON.parse(raw) as SmileyFrequency) } : { ...SEED };
  } catch {
    // Private mode, a disabled store, or something that is not JSON any more.
    return { ...SEED };
  }
}

function persist(frequency: SmileyFrequency): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(frequency));
  } catch {
    // Not worth interrupting a message over.
  }
}

let frequency = load();
let ranked = rankSlugs(frequency);
const listeners = new Set<() => void>();

/** Stable between changes, which is what useSyncExternalStore requires. */
export function rankedSmileys(): Smiley[] {
  return ranked;
}

export function topSmileys(count: number): Smiley[] {
  return ranked.slice(0, count);
}

/** One more use of this smiley: sending it in a message, or picking it as a reaction. */
export function bumpSmiley(slug: string): void {
  frequency = { ...frequency, [slug]: (frequency[slug] ?? 0) + 1 };
  persist(frequency);
  ranked = rankSlugs(frequency);
  for (const listener of listeners) listener();
}

/** Back to the starting order, for the reader who wants their «Часто» honest again. */
export function resetSmileyStats(): void {
  frequency = { ...SEED };
  persist(frequency);
  ranked = rankSlugs(frequency);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The ranking, re-rendering whoever reads it as soon as anything is bumped. */
export function useSmileyStats(): Smiley[] {
  return useSyncExternalStore(subscribe, rankedSmileys, rankedSmileys);
}
